import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Self,
  SimpleChanges,
  TemplateRef,
  ChangeDetectorRef, ViewChild, QueryList
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { SelectOptionModel } from '@shared/components/form-elements/select/model/select.option.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HIDE_REQUIRED_MARKER } from '@shared/util/form.util';
import {MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import { isRequired } from '../form-elements.util';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, startWith, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { MatOption } from '@angular/material/core';

@UntilDestroy()
@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent<T> implements OnInit, OnChanges, ControlValueAccessor {
  @Input() label: string;
  @Input() options: SelectOptionModel<T>[];
  @Input() multiple = false;
  @Input() isFullWide: boolean;
  @Input() disabled: boolean;
  @Input() placeholder: string;
  @Input() tooltip: string;
  @Input() enableAutocomplete = true;
  @Input() defaultTemplateTooltipVisible: boolean = true;
  @Input() panelClassVisible: boolean = true;
  @Output() selectionChanged = new EventEmitter<any>();

  @ContentChild(TemplateRef) templateRef: TemplateRef<any>;
  required: boolean;
  hideRequiredMarker = HIDE_REQUIRED_MARKER;

  private searchSubject$ = new BehaviorSubject<string>('');
  @ViewChild(MatAutocompleteTrigger) trigger: MatAutocompleteTrigger;
  keyManager: ActiveDescendantKeyManager<MatOption>;
  filteredOptions$: Observable<SelectOptionModel<T>[]>;
  private onChange: (value: T) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(@Self() public ngControl: NgControl, private cdr: ChangeDetectorRef) {
    ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.required = isRequired(this.ngControl);

    this.ngControl.statusChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.required = isRequired(this.ngControl));

    if (this.enableAutocomplete) {
      this.filteredOptions$ = this.searchSubject$.pipe(
        startWith(''),
        map(value => {
          const filterValue = typeof value === 'string' ? value : '';
          return this._filterOptions(filterValue);
        }),
        shareReplay(1),
        untilDestroyed(this)
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      if (this.disabled) {
        this.ngControl?.control?.disable();
      } else {
        this.ngControl?.control?.enable();
      }
    }


    if (changes.options && this.enableAutocomplete) {
      this.searchSubject$.next('');
      this.cdr.markForCheck();
    }
  }

  writeValue(value: T): void {
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: T) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
  }

  onFocus(event: any): void {
    const searchInput = event?.target as HTMLInputElement;
    if (!this.disabled) {
     this.searchSubject$.next('');
      const currentValue = this.ngControl.control?.value;
      if (currentValue) {
        searchInput?.select();
      }
    }
  }

  onPanelOpened(): void {
    setTimeout(() => {
      const options: QueryList<MatOption> = this.trigger.autocomplete.options;
      if (options?.length) {
        this.keyManager = new ActiveDescendantKeyManager(options).withVerticalOrientation();
        this.highlightSelectedOption(options);
      }
    });
  }

  highlightSelectedOption(options: QueryList<MatOption>): void {
    const currentValue = this.ngControl.control?.value;
    if (!currentValue || !this.keyManager) {
      return;
    }
    const currentOptions = options.toArray();
    const index = currentOptions.findIndex(o => o.value === currentValue);

    if (index > -1) {
      this.keyManager.setActiveItem(index);
      this.trigger.autocomplete._keyManager = this.keyManager;
      this.scrollActiveOptionIntoView();
    }
  }


  scrollActiveOptionIntoView(): void {
    const active = this.keyManager.activeItem;
    const el = active ? document.getElementById(active.id) : null;
    el?.scrollIntoView({ block: 'center' });
  }

  onBlur(event?: any): void {
    const searchInput = event?.target as HTMLInputElement;
    const inputValue = searchInput?.value || '';

    const matchingOption = this.options?.find(option =>
      String(option.data).toLowerCase() === inputValue.toLowerCase()
    );
    if (!matchingOption) {
     searchInput.value = '';
      this.onChange(null);
    }
    this.onTouched();
    this.cdr.markForCheck();
  }

  onInput(event: Event): void {
    const searchInput = event.target as HTMLInputElement;
    this.searchSubject$.next(searchInput.value);
  }

  onAutocompleteOptionSelected(event: MatAutocompleteSelectedEvent, searchInput: HTMLInputElement): void {
    const selectedValue = event.option.value;

    this.onChange(selectedValue);
    this.searchSubject$.next('');
    this.selectionChanged.emit(event);
    this.onTouched();
    this.cdr.markForCheck();
    setTimeout(() => searchInput.blur(), 0);
  }

  displayFn(value: T): string {
    if (value === null || value === undefined || !this.options) {
      return '';
    }
    const option = this.options.find(opt => opt.value === value);
    return option ? String(option.data) : '';
  }

  private _filterOptions(filterValue: string): SelectOptionModel<T>[] {
    if (!filterValue || !this.options) {
      return this.options || [];
    }

    const filterValueLower = filterValue.toLowerCase();
    return this.options.filter(option =>
      String(option.data).toLowerCase().includes(filterValueLower)
    );
  }
}
